import CancelIcon from '@mui/icons-material/Clear'
import SearchIcon from '@mui/icons-material/Search'
import { Box, IconButton, InputAdornment, TextField, Typography } from '@mui/material'
import {
    DataGridPro,
    DataGridProProps,
    GridCellEditCommitParams,
    GridSelectionModel,
    useGridApiRef,
} from '@mui/x-data-grid-pro'
import React, { useEffect, useState } from 'react'

import getUserColumns from './manage-users/getUserColumns'

const escapeRegExp = (value: string) => {
    return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')
}

interface DataTableProps extends DataGridProProps {
    title: string
    extraActions: React.JSX.Element[]
    onSelectRow: (model: GridSelectionModel) => void
    onEdit?: (params: GridCellEditCommitParams) => void
    onSearch?: (searchTerm: string) => void
}

export default function DataTable({
    rows,
    title,
    onEdit,
    columns,
    loading,
    className,
    onSelectRow,
    extraActions,
    onSearch,
    ...rest
}: DataTableProps) {
    const [pageSize, setPageSize] = useState(50)
    const [searchBarText, setSearchBarText] = useState('')

    const userColumns = getUserColumns()

    const dataGridRef = useGridApiRef()
    const pageSizeOptions = [50, 100, 150]

    const handleSearch = (searchText: string) => {
        setSearchBarText(searchText)
        const searchRegex = new RegExp(escapeRegExp(searchText), 'i')
        const searchResults = rows.filter(result => {
            return Object.keys(result).some(field => {
                return userColumns.find(column => column.field === field && !column?.hide)
                    ? searchRegex.test(result[field]?.toString())
                    : false
            })
        })
        dataGridRef.current.setRows(searchResults)
        if (onSearch) onSearch(searchText)
    }

    useEffect(() => {
        if (searchBarText) handleSearch(searchBarText)
    }, [rows])

    return (
        <Box sx={{ display: 'flex', flexFlow: 'column', flex: 1, height: '100%' }}>
            <Box
                sx={{
                    mb: 1,
                    flex: 0,
                    minHeight: 50,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexFlow: {
                        sm: 'column nowrap',
                        md: 'row nowrap',
                    },
                }}
            >
                <Typography variant="h5">{title}</Typography>
                <Box sx={{ marginLeft: 1, flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <TextField
                        sx={{
                            minWidth: 286,
                            '& .MuiInputBase-root': { padding: '0px 0px 0px 8px', position: 'relative' },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 28 }} />
                                </InputAdornment>
                            ),
                            endAdornment: searchBarText && (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => {
                                            handleSearch('')
                                        }}
                                    >
                                        <CancelIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        label="Search"
                        size="small"
                        variant="outlined"
                        value={searchBarText}
                        onChange={e => handleSearch(e.target.value)}
                    />
                </Box>
                <Box sx={{ display: 'flex', flex: 0, alignItems: 'center' }}>
                    {extraActions && extraActions.map(action => action)}
                </Box>
            </Box>
            <DataGridPro
                pagination
                rows={rows}
                loading={loading}
                columns={columns}
                checkboxSelection
                pageSize={pageSize}
                apiRef={dataGridRef}
                disableSelectionOnClick
                onCellEditCommit={onEdit}
                rowsPerPageOptions={pageSizeOptions}
                onSelectionModelChange={onSelectRow}
                onPageSizeChange={newPageSize => setPageSize(newPageSize)}
                {...rest}
            />
        </Box>
    )
}
